<template>
  <div class="young-person-profile">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Chargement du profil...</p>
    </div>

    <!-- Main Content -->
    <div v-else-if="profile" class="profile-container">
      <!-- Back button at top left outside header -->
      <button class="btn-back" @click="goBack">
        <span class="back-arrow">←</span>
        Retour
      </button>

      <!-- Header with young person info -->
      <div class="profile-header">
        <div class="young-person-info">
          <div class="avatar-large" v-if="!profilePicture">
            {{ initials }}
          </div>
          <img v-else :src="profilePicture" :alt="fullName" class="avatar-large-img" />
          <div class="person-details">
            <h1 class="person-name">{{ fullName }}</h1>
            <p class="person-meta">{{ profile.user.age }} ans</p>

            <div v-if="profile.attachmentCode" class="attachment-code-display">
              <span class="code-label">Code d'attachement:</span>
              <span class="code-value">{{ profile.attachmentCode }}</span>
            </div>

            <div v-if="profile.passions && profile.passions.length > 0" class="passions-list">
              <span class="passions-label">Passions:</span>
              <div class="passion-badges">
                <span v-for="(passion, index) in profile.passions" :key="index" class="passion-badge">
                  {{ passion }}
                </span>
              </div>
            </div>

            <div v-if="profile.accompanists && profile.accompanists.length > 0" class="accompanists-list">
              <span class="accompanists-label">Accompagné par:</span>
              <div class="accompanist-badges">
                <span
                  v-for="acc in profile.accompanists"
                  :key="acc.id"
                  class="accompanist-badge"
                  :class="`type-${acc.type}`"
                  :title="`${acc.name} - ${acc.type}`"
                >
                  {{ acc.name }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="header-actions">
          <button class="btn-disconnect" @click="confirmDisconnect" title="Déconnecter ce jeune">
            <span class="btn-icon">🚪</span>
            Déconnecter
          </button>
        </div>
      </div>

      <!-- Tabs Navigation -->
      <div class="tabs-container">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          :class="['tab-button', { active: activeTab === tab.id }]"
          @click="activeTab = tab.id"
        >
          <span class="tab-icon">{{ tab.icon }}</span>
          <span class="tab-label">{{ tab.label }}</span>
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Vue d'ensemble -->
        <div v-if="activeTab === 'overview'" class="tab-panel">
          <div class="overview-grid">
            <!-- Résumé synthétique -->
            <div class="section-card summary-card full-width">
              <h2 class="section-title">
                <span class="title-icon">📊</span>
                Résumé du parcours
              </h2>
              <div class="summary-content">
                <div class="summary-main">
                  <div class="summary-stats">
                    <div class="summary-stat-item">
                      <div class="summary-stat-value">{{ overallGameProgress }}%</div>
                      <div class="summary-stat-label">Progression globale des jeux</div>
                      <div class="summary-progress-bar">
                        <div class="summary-progress-fill" :style="{ width: overallGameProgress + '%' }"></div>
                      </div>
                    </div>

                    <div class="summary-stat-item">
                      <div class="summary-stat-value">{{ displayCompetencesValue }}</div>
                      <div class="summary-stat-label">{{ displayCompetencesLabel }}</div>
                      <div class="summary-progress-bar">
                        <div
                          class="summary-progress-fill"
                          :style="{ width: displayCompetencesProgress + '%' }"
                        ></div>
                      </div>
                    </div>

                    <div class="summary-stat-item">
                      <div class="summary-stat-value">{{ totalObservationsCount }}</div>
                      <div class="summary-stat-label">Observations réalisées</div>
                    </div>

                    <div class="summary-stat-item">
                      <div class="summary-stat-value">{{ unlockedBadges.length }}</div>
                      <div class="summary-stat-label">Badges débloqués</div>
                    </div>
                  </div>

                  <div v-if="profileSummaryText" class="summary-description">
                    <div class="summary-description-header">
                      <span class="description-icon">💭</span>
                      <h3>Synthèse du profil</h3>
                    </div>
                    <p>{{ profileSummaryText }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Compétences - État d'évaluation -->
            <div class="section-card competences-status-card">
              <h2 class="section-title">
                <span class="title-icon">⭐</span>
                État des compétences
              </h2>

              <!-- Auto-évaluation du jeune -->
              <div v-if="totalSkillsFromAssessment > 0" class="skills-assessment-stats">
                <h3 class="subsection-title">Auto-évaluation du jeune</h3>
                <div class="competences-status-grid">
                  <div class="competence-status-item validated">
                    <div class="status-icon-wrapper">
                      <span class="status-icon">✓</span>
                    </div>
                    <div class="status-content">
                      <div class="status-value">{{ evaluatedSkillsCount }}/{{ totalSkillsFromAssessment }}</div>
                      <div class="status-label">Évaluées par vous</div>
                    </div>
                  </div>

                  <div class="competence-status-item to-evaluate">
                    <div class="status-icon-wrapper">
                      <span class="status-icon">○</span>
                    </div>
                    <div class="status-content">
                      <div class="status-value">{{ notEvaluatedSkillsCount }}</div>
                      <div class="status-label">À évaluer</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Compétences du référentiel -->
              <div v-if="totalCompetences > 0" class="skills-assessment-stats" :class="{ 'with-margin-top': totalSkillsFromAssessment > 0 }">
                <h3 class="subsection-title">Compétences du référentiel</h3>
                <div class="competences-status-grid">
                  <div class="competence-status-item validated">
                    <div class="status-icon-wrapper">
                      <span class="status-icon">✓</span>
                    </div>
                    <div class="status-content">
                      <div class="status-value">{{ totalCompetencesValidated }}/{{ totalCompetences }}</div>
                      <div class="status-label">Validées</div>
                    </div>
                  </div>

                  <div class="competence-status-item to-evaluate">
                    <div class="status-icon-wrapper">
                      <span class="status-icon">○</span>
                    </div>
                    <div class="status-content">
                      <div class="status-value">{{ competencesNonValidated }}</div>
                      <div class="status-label">Non validées</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Empty state -->
              <div v-if="totalSkillsFromAssessment === 0 && totalCompetences === 0" class="empty-competences-state">
                <p>Aucune compétence à afficher pour le moment.</p>
              </div>

              <button class="btn-view-competences" @click="activeTab = 'competences'">
                Voir les détails des compétences →
              </button>
            </div>

            <!-- Observations récentes -->
            <div v-if="recentObservations.length > 0" class="section-card observations-overview-card full-width">
              <h2 class="section-title">
                <span class="title-icon">📝</span>
                Dernières observations ({{ totalObservationsCount }})
              </h2>
              <div class="observations-list-compact">
                <ObservationCard
                  v-for="obs in recentObservations.slice(0, 3)"
                  :key="obs.id"
                  :observation="obs"
                  :show-actions="false"
                />
                <button v-if="recentObservations.length > 0" class="btn-see-all" @click="activeTab = 'observations'">
                  Voir toutes les observations ({{ totalObservationsCount }}) →
                </button>
              </div>
            </div>

            <!-- Empty state for overview if no data -->
            <div v-if="Object.keys(gamesData).length === 0 && !profile.competenceValidations?.length && recentObservations.length === 0" class="section-card full-width">
              <div class="empty-overview">
                <div class="empty-icon">📊</div>
                <h3>Profil en cours de construction</h3>
                <p>{{ profile.user.firstName }} n'a pas encore commencé son parcours. Les données apparaîtront ici au fur et à mesure de sa progression.</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Observations -->
        <div v-if="activeTab === 'observations'" class="tab-panel">
          <div class="observations-header">
            <h2 class="section-title">
              <span class="title-icon">📝</span>
              Toutes les observations ({{ totalObservationsCount }})
            </h2>
            <button class="btn-primary" @click="newObservation">
              <span class="btn-icon-plus">+</span>
              <span class="btn-text-observation">Nouvelle observation</span>
            </button>
          </div>

          <div class="observations-grid">
            <ObservationCard
              v-for="obs in recentObservations"
              :key="obs.id"
              :observation="obs"
              @view-details="viewObservationDetails" @edit="editObservation" @delete="deleteObservation"
            />
          </div>

          <div v-if="recentObservations.length === 0" class="empty-observations">
            <div class="empty-icon">📝</div>
            <h3>Aucune observation</h3>
            <p>Commencez à documenter les compétences observées chez {{ profile.user.firstName }}</p>
            <button class="btn-primary" @click="newObservation">
              Créer la première observation
            </button>
          </div>
        </div>

        <!-- Compétences détaillées -->
        <div v-if="activeTab === 'competences'" class="tab-panel">
          <!-- Auto-évaluation des compétences -->
          <div v-if="skillsByCategory" class="skills-self-assessment-section">
            <div class="assessment-header">
              <h2 class="section-title">
                <span class="title-icon">⭐</span>
                Auto-évaluation du jeune
              </h2>
              <p class="assessment-description">
                Le jeune a identifié ses compétences. Vous pouvez évaluer votre perception de ces compétences de 0 à 5 étoiles et ajouter un commentaire basé sur vos observations.
              </p>
            </div>

            <!-- Filtres -->
            <div class="skills-filters-container">
              <div class="filter-group">
                <label class="filter-label">État :</label>
                <div class="filter-buttons">
                  <button
                    :class="['filter-btn', { active: skillsFilter === 'all' }]"
                    @click="skillsFilter = 'all'"
                  >
                    Toutes
                  </button>
                  <button
                    :class="['filter-btn', { active: skillsFilter === 'evaluated' }]"
                    @click="skillsFilter = 'evaluated'"
                  >
                    ✓ Évaluées ({{ evaluatedSkillsCount }})
                  </button>
                  <button
                    :class="['filter-btn', { active: skillsFilter === 'non-evaluated' }]"
                    @click="skillsFilter = 'non-evaluated'"
                  >
                    ○ À évaluer ({{ notEvaluatedSkillsCount }})
                  </button>
                </div>
              </div>

              <div class="filter-group">
                <label class="filter-label">Catégorie :</label>
                <div class="filter-buttons">
                  <button
                    :class="['filter-btn', { active: selectedCategoryFilter === 'all' }]"
                    @click="selectedCategoryFilter = 'all'"
                  >
                    Toutes
                  </button>
                  <button
                    v-for="category in skillsByCategory"
                    :key="category.key"
                    :class="['filter-btn', { active: selectedCategoryFilter === category.key }]"
                    @click="selectedCategoryFilter = category.key"
                  >
                    {{ category.icon }} {{ category.label }}
                  </button>
                </div>
              </div>

              <div class="filter-group">
                <label class="filter-label">Affichage :</label>
                <div class="filter-buttons">
                  <button
                    :class="['filter-btn', { active: showOtherEvaluations }]"
                    @click="showOtherEvaluations = !showOtherEvaluations"
                  >
                    {{ showOtherEvaluations ? 'Masquer les évaluations des autres' : 'Afficher les évaluations des autres' }}
                  </button>
                </div>
              </div>
            </div>

            <div class="skills-categories-grid">
              <div
                v-for="category in filteredSkillsByCategory"
                :key="category.key"
                class="skill-category-card"
                v-show="shouldShowCategory(category)"
              >
                <div class="category-header-skill">
                  <span class="category-icon-skill">{{ category.icon }}</span>
                  <h3 class="category-title-skill" :style="{ color: category.color }">
                    {{ category.label }}
                  </h3>
                </div>

                <!-- Compétences non évaluées -->
                <div v-if="getNonEvaluatedSkills(category.skills).length > 0 && shouldShowNonEvaluated()" class="skills-section">
                  <h4 class="skills-section-title">
                    <span class="section-icon">○</span>
                    À évaluer ({{ getNonEvaluatedSkills(category.skills).length }})
                  </h4>
                  <div class="skills-list">
                    <div
                      v-for="skill in getNonEvaluatedSkills(category.skills)"
                      :key="skill"
                      class="skill-row non-evaluated"
                      :style="{ borderLeftColor: category.color }"
                    >
                      <div class="skill-main-content">
                        <div class="skill-header-row">
                          <div class="skill-title-section">
                            <span class="skill-name">{{ skill }}</span>
                            <span v-if="getSkillSelfAssessment(skill)" class="skill-badge" :style="{ backgroundColor: getSkillSelfAssessment(skill).color + '20', color: getSkillSelfAssessment(skill).color }">
                              Auto-évaluation du jeune : {{ getSkillSelfAssessment(skill).icon }} {{ getSkillSelfAssessment(skill).label }}
                            </span>
                          </div>
                          <button
                            class="btn-evaluate"
                            @click="openEvaluationModal(skill, category.label)"
                          >
                            <span class="btn-icon">⭐</span>
                            Évaluer
                          </button>
                        </div>

                        <!-- Other evaluations -->
                        <div v-if="showOtherEvaluations && accompanistRatings[skill]?.others?.length > 0" class="other-evaluations">
                          <h5 class="other-evaluations-title">Autres évaluations ({{ accompanistRatings[skill].others.length }})</h5>
                          <div
                            v-for="(otherRating, idx) in accompanistRatings[skill].others"
                            :key="idx"
                            class="other-evaluation-item"
                            :class="{ 'has-comment': otherRating.comment, 'expanded': expandedOtherComments[otherRating.id] }"
                            @click.stop="otherRating.comment && toggleOtherComment(otherRating.id)"
                          >
                            <div class="other-evaluation-header">
                              <span class="evaluator-name">{{ otherRating.issuer?.first_name }} {{ otherRating.issuer?.last_name }}</span>
                              <StarRating :stars="otherRating.rating" :size="16" />
                            </div>
                            <div class="evaluation-meta">
                               <span class="evaluation-date" style="font-size: 0.75rem;">
                                 {{ formatDate(otherRating.dateUpdated) }}
                               </span>
                               <span v-if="otherRating.comment" class="comment-indicator" @click.stop="toggleOtherComment(otherRating.id)">
                                 <span class="comment-icon">💬</span>
                                 {{ expandedOtherComments[otherRating.id] ? 'Masquer' : 'Voir commentaire' }}
                                 <span class="expand-arrow">{{ expandedOtherComments[otherRating.id] ? '▼' : '▶' }}</span>
                               </span>
                            </div>
                            <transition name="slide-down">
                              <p v-if="otherRating.comment && expandedOtherComments[otherRating.id]" class="other-evaluation-comment">
                                {{ otherRating.comment }}
                              </p>
                            </transition>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Compétences évaluées -->
                <div v-if="getEvaluatedSkills(category.skills).length > 0 && shouldShowEvaluated()" class="skills-section">
                  <h4 class="skills-section-title">
                    <span class="section-icon">✓</span>
                    Évaluées ({{ getEvaluatedSkills(category.skills).length }})
                  </h4>
                  <div class="skills-list">
                    <div
                      v-for="skill in getEvaluatedSkills(category.skills)"
                      :key="skill"
                      class="skill-row evaluated"
                      :style="{ borderLeftColor: category.color }"
                    >
                      <div class="skill-main-content">
                        <div class="skill-header-row">
                          <div class="skill-title-section">
                            <span class="skill-name">{{ skill }}</span>
                            <span v-if="getSkillSelfAssessment(skill)" class="skill-badge" :style="{ backgroundColor: getSkillSelfAssessment(skill).color + '20', color: getSkillSelfAssessment(skill).color }">
                              Auto-évaluation du jeune : {{ getSkillSelfAssessment(skill).icon }} {{ getSkillSelfAssessment(skill).label }}
                            </span>
                          </div>
                          <button
                            class="btn-evaluate"
                            @click="openEvaluationModal(skill, category.label)"
                          >
                            <span class="btn-icon">✏️</span>
                            Modifier
                          </button>
                        </div>
                        <div
                          class="existing-evaluation"
                          :class="{ 'has-comment': accompanistRatings[skill]?.myRating?.comment, 'expanded': expandedComments[skill] }"
                          @click.stop="toggleComment(skill)"
                        >
                          <div class="evaluation-summary">
                            <div class="evaluation-content">
                              <span class="evaluation-label">Votre évaluation:</span>
                              <StarRating :stars="accompanistRatings[skill]?.myRating?.rating || 0" :color="category.color" />
                            </div>
                            <div class="evaluation-meta">
                              <span class="evaluation-date">
                                <span class="date-icon">📅</span>
                                {{ formatDate(accompanistRatings[skill]?.myRating?.dateUpdated) }}
                              </span>
                              <span v-if="accompanistRatings[skill]?.myRating?.comment" class="comment-indicator">
                                <span class="comment-icon">💬</span>
                                {{ expandedComments[skill] ? 'Masquer' : 'Voir commentaire' }}
                                <span class="expand-arrow">{{ expandedComments[skill] ? '▼' : '▶' }}</span>
                              </span>
                            </div>
                          </div>
                          <transition name="slide-down">
                            <p v-if="accompanistRatings[skill]?.myRating?.comment && expandedComments[skill]" class="evaluation-comment">
                              {{ accompanistRatings[skill].myRating.comment }}
                            </p>
                          </transition>
                        </div>
                        <!-- Other evaluations -->
                        <div v-if="showOtherEvaluations && accompanistRatings[skill]?.others?.length > 0" class="other-evaluations">
                          <h5 class="other-evaluations-title">Autres évaluations ({{ accompanistRatings[skill].others.length }})</h5>
                          <div
                            v-for="(otherRating, idx) in accompanistRatings[skill].others"
                            :key="idx"
                            class="other-evaluation-item"
                            :class="{ 'has-comment': otherRating.comment, 'expanded': expandedOtherComments[otherRating.id] }"
                            @click.stop="otherRating.comment && toggleOtherComment(otherRating.id)"
                          >
                            <div class="other-evaluation-header">
                              <span class="evaluator-name">{{ otherRating.issuer?.first_name }} {{ otherRating.issuer?.last_name }}</span>
                              <StarRating :stars="otherRating.rating" :size="16" />
                            </div>
                            <div class="evaluation-meta">
                               <span class="evaluation-date" style="font-size: 0.75rem;">
                                 {{ formatDate(otherRating.dateUpdated) }}
                               </span>
                               <span v-if="otherRating.comment" class="comment-indicator" @click.stop="toggleOtherComment(otherRating.id)">
                                 <span class="comment-icon">💬</span>
                                 {{ expandedOtherComments[otherRating.id] ? 'Masquer' : 'Voir commentaire' }}
                                 <span class="expand-arrow">{{ expandedOtherComments[otherRating.id] ? '▼' : '▶' }}</span>
                               </span>
                            </div>
                            <transition name="slide-down">
                              <p v-if="otherRating.comment && expandedOtherComments[otherRating.id]" class="other-evaluation-comment">
                                {{ otherRating.comment }}
                              </p>
                            </transition>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="skillsAssessmentCompletion === 0" class="empty-skills-assessment">
            <div class="empty-icon">⭐</div>
            <h3>Auto-évaluation non complétée</h3>
            <p>{{ profile.user.firstName }} n'a pas encore complété l'auto-évaluation des compétences.</p>
          </div>

          <div class="competences-detailed">
            <div
              v-for="validation in profile.competenceValidations"
              :key="validation.blockId"
              class="block-section"
            >
              <div class="block-header-large">
                <div class="block-icon-large" :style="{ backgroundColor: getBlockInfo(validation.blockId).color + '20' }">
                  <span>{{ getBlockInfo(validation.blockId).icon }}</span>
                </div>
                <div class="block-title-info">
                  <h2>{{ validation.blockName }}</h2>
                  <p>{{ getBlockInfo(validation.blockId).description }}</p>
                </div>
                <div class="block-stats-large">
                  <div class="stat-item-large">
                    <span class="stat-label">Validées</span>
                    <span class="stat-value">{{ validation.competences.filter(c => c.validated).length }}/{{ validation.competences.length }}</span>
                  </div>
                </div>
              </div>

              <div class="competences-table">
                <div
                  v-for="comp in validation.competences"
                  :key="comp.key"
                  class="competence-row"
                  :class="{ validated: comp.validated }"
                >
                  <div class="comp-main-info">
                    <div class="comp-name-section">
                      <span v-if="comp.validated" class="comp-badge-check">✓</span>
                      <div>
                        <div class="comp-name">{{ comp.name }}</div>
                        <div class="comp-observations-count">{{ comp.observationsCount }} observations</div>
                      </div>
                    </div>

                    <StarRating :stars="comp.stars" />
                  </div>

                  <div class="comp-scores">
                    <div class="score-item">
                      <span class="score-label">Score jeux</span>
                      <span class="score-value">{{ comp.gameScore }}/20</span>
                    </div>
                    <div class="progress-indicator">
                      <div
                        class="progress-fill"
                        :style="{
                          width: (comp.stars / 5 * 100) + '%',
                          backgroundColor: getBlockInfo(validation.blockId).color
                        }"
                      ></div>
                    </div>
                  </div>

                  <div class="comp-actions">
                    <button class="btn-add-obs" @click="newObservationForCompetence(comp)">
                      + Observation
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Parcours jeux -->
        <div v-if="activeTab === 'games'" class="tab-panel">
          <div class="games-dashboard">
            <div class="games-header">
              <h2 class="section-title">
                <span class="title-icon">🎮</span>
                Résultats des jeux du parcours
              </h2>
              <div class="overall-progress">
                <span class="progress-label">Progression globale des jeux:</span>
                <div class="progress-bar-inline">
                  <div class="progress-fill-inline" :style="{ width: overallGameProgress + '%' }"></div>
                </div>
                <span class="progress-percentage">{{ overallGameProgress }}%</span>
              </div>
            </div>

            <!-- All games in grid -->
            <div class="games-grid-all">
              <!-- Scenario Game -->
              <div
                v-if="gamesData.scenario"
                class="game-simple-card"
                :class="{ 'game-completed': gamesData.scenario.completion >= 1.0 }"
              >
                <h4>
                  {{ getGameName('scenario') }}
                  <span v-if="gamesData.scenario.completion >= 1.0" class="trophy-icon">🏆</span>
                </h4>
                <div class="game-completion-circle">
                  <svg width="120" height="120">
                    <circle cx="60" cy="60" r="50" fill="none" stroke="#E5E7EB" stroke-width="10"/>
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      :stroke="gamesData.scenario.completion >= 1.0 ? '#F59E0B' : '#FF9800'"
                      stroke-width="10"
                      :stroke-dasharray="314"
                      :stroke-dashoffset="314 - (314 * gamesData.scenario.completion)"
                      transform="rotate(-90 60 60)"
                    />
                  </svg>
                  <div class="circle-text" :class="{ 'text-completed': gamesData.scenario.completion >= 1.0 }">
                    {{ Math.round(gamesData.scenario.completion * 100) }}%
                  </div>
                </div>
              </div>

              <!-- Other games -->
              <div
                v-for="(game, key) in filteredGamesData"
                :key="key"
                class="game-simple-card"
                :class="{ 'game-completed': game.completion >= 1.0 }"
              >
                <h4>
                  {{ getGameName(key) }}
                  <span v-if="game.completion >= 1.0" class="trophy-icon">🏆</span>
                </h4>
                <div class="game-completion-circle">
                  <svg width="120" height="120">
                    <circle cx="60" cy="60" r="50" fill="none" stroke="#E5E7EB" stroke-width="10"/>
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      :stroke="game.completion >= 1.0 ? '#F59E0B' : '#FF9800'"
                      stroke-width="10"
                      :stroke-dasharray="314"
                      :stroke-dashoffset="314 - (314 * game.completion)"
                      transform="rotate(-90 60 60)"
                    />
                  </svg>
                  <div class="circle-text" :class="{ 'text-completed': game.completion >= 1.0 }">
                    {{ Math.round(game.completion * 100) }}%
                  </div>
                </div>
              </div>
            </div>

            <!-- Badges Summary in Games Tab -->
            <div v-if="unlockedBadges.length > 0" class="badges-summary-box">
              <div class="badges-summary-header">
                <span class="summary-icon">🏆</span>
                <h3>{{ unlockedBadges.length }} Badge{{ unlockedBadges.length > 1 ? 's' : '' }} débloqué{{ unlockedBadges.length > 1 ? 's' : '' }}</h3>
              </div>
              <div class="badges-summary-list">
                <div
                  v-for="badge in unlockedBadges"
                  :key="badge.key"
                  class="badge-display-card"
                >
                  <div class="badge-glow"></div>
                  <div class="badge-emblem">
                    <img
                      :src="badge.image"
                      :alt="`Badge ${badge.name}`"
                      class="badge-emblem-img"
                    />
                  </div>
                  <div class="badge-details">
                    <span class="badge-name">{{ badge.name }}</span>
                    <span class="badge-caption">Badge débloqué</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="info-box">
              <span class="info-icon">ℹ️</span>
              <p>Les résultats des jeux sont utilisés comme indicateurs complémentaires aux observations. Ils permettent d'identifier des pistes de compétences à observer en situation réelle.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Input Method Choice Modal -->
    <div v-if="showInputMethodModal" class="modal-overlay" @click="closeInputMethodModal">
      <div class="modal-content input-method-modal" @click.stop>
        <div class="modal-header">
          <h2>Comment souhaitez-vous créer l'observation ?</h2>
          <button class="modal-close" @click="closeInputMethodModal">✕</button>
        </div>

        <div class="modal-body">
          <p class="input-method-description">
            Choisissez la méthode de saisie qui vous convient le mieux
          </p>

          <div class="input-method-options">
            <button class="input-method-card" @click="chooseVoiceInput">
              <div class="method-icon voice-icon">🎤</div>
              <h3 class="method-title">Saisie vocale</h3>
              <p class="method-description">
                Dictez votre observation à l'oral, nous la transcrirons automatiquement
              </p>
              <span class="method-badge">Disponible</span>
            </button>

            <button class="input-method-card" @click="chooseManualInput">
              <div class="method-icon manual-icon">✍️</div>
              <h3 class="method-title">Saisie manuelle</h3>
              <p class="method-description">
                Remplissez le formulaire d'observation en quelques clics
              </p>
              <span class="method-badge">Disponible</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Evaluation Modal -->
    <div v-if="showEvaluationModal" class="modal-overlay" @click="closeEvaluationModal">
      <div class="modal-content evaluation-modal" @click.stop>
        <div class="modal-header">
          <h2>Évaluer la compétence</h2>
          <button class="modal-close" @click="closeEvaluationModal">✕</button>
        </div>

        <div class="modal-body">
          <div class="skill-being-evaluated">
            <h3>{{ currentSkill }}</h3>
            <p class="skill-context">Auto-évaluation du jeune: {{ currentSkillCategory }}</p>
          </div>

          <div class="evaluation-form">
            <div class="form-group">
              <label class="form-label">Votre évaluation (0-5 étoiles)</label>
              <div class="rating-input-large">
                <EditableStarRating
                  v-model="evaluationForm.rating"
                  :show-count="true"
                />
              </div>
              <p class="rating-help">
                0 = Non observé | 1 = Débutant | 2 = En développement | 3 = Acquis | 4 = Maîtrisé | 5 = Expert
              </p>
            </div>

            <div class="form-group">
              <label class="form-label">Commentaire (optionnel)</label>
              <textarea
                v-model="evaluationForm.comment"
                class="form-textarea"
                placeholder="Décrivez ce que vous avez observé concernant cette compétence..."
                rows="5"
              ></textarea>
              <p class="char-count">{{ evaluationForm.comment.length }} caractères</p>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="modal-footer-left">
            <button
              v-if="accompanistRatings[currentSkill]"
              class="btn-delete"
              @click="deleteEvaluation"
              title="Supprimer cette évaluation"
            >
              🗑️ Supprimer
            </button>
          </div>
          <div class="modal-footer-right">
            <button class="btn-secondary" @click="closeEvaluationModal">Annuler</button>
            <button
              class="btn-primary"
              @click="saveEvaluation"
              :disabled="evaluationForm.rating === 0"
            >
              Enregistrer l'évaluation
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Observation Form Modal -->
    <div v-if="showObservationForm" class="modal-overlay" @click="closeObservationForm">
      <div class="modal-content observation-form-modal" @click.stop>
        <ObservationForm
          v-if="profile"
          :young-person-id="profile.user.id"
          :young-person-name="profile.user.firstName"
          :preselected-competence="preselectedCompetence"
          :start-in-vocal-mode="openInVocalMode"
          :hide-vocal-mode="!openInVocalMode"
          :existing-observation="editingObservation"
          @save="handleObservationSubmit"
          @close="closeObservationForm"
        />
      </div>
    </div>

    <!-- Observation Detail Modal -->
    <div v-if="showObservationDetail" class="modal-overlay" @click="closeObservationDetail">
      <div class="modal-content observation-detail-modal" @click.stop>
        <ObservationDetail
          v-if="selectedObservation"
          :observation="selectedObservation"
          @close="closeObservationDetail" @edit="editObservation" @delete="deleteObservation"
        />
      </div>
    </div>

    <!-- Confirm Delete Modal -->
    <div v-if="showConfirmDeleteModal && observationToDelete" class="modal-overlay" @click="cancelDelete">
      <div class="modal-content confirm-modal" @click.stop>
        <div class="modal-header">
          <h2>Confirmer la suppression</h2>
        </div>
        <div class="modal-body">
          <div class="warning-icon-wrapper">
            <span class="warning-icon">⚠️</span>
          </div>
          <p class="confirm-message">
            Êtes-vous sûr de vouloir supprimer cette observation du
            <strong>{{ new Date(observationToDelete.date).toLocaleDateString('fr-FR') }}</strong> ?
          </p>
          <p class="confirm-warning">
            Cette action est irréversible.
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="cancelDelete">
            Annuler
          </button>
          <button class="btn-danger" @click="confirmDelete">
            <span class="btn-icon">🗑️</span>
            Supprimer
          </button>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <div v-if="showSuccessModal && successMessageData" class="modal-overlay" @click="closeSuccessModal">
      <div class="modal-content success-modal" @click.stop>
        <div class="modal-body success-body">
          <div class="success-icon-wrapper">
            <span class="success-icon-large">✅</span>
          </div>
          <h2>Observation enregistrée !</h2>
          
          <div class="success-summary">
            <div class="summary-row">
              <span class="summary-label">Date :</span>
              <span class="summary-value">{{ successMessageData.date }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Durée :</span>
              <span class="summary-value">{{ successMessageData.duration }}</span>
            </div>
            <div class="summary-row full">
              <span class="summary-label">Compétences :</span>
              <span class="summary-value">{{ successMessageData.competences }}</span>
            </div>
          </div>

          <button class="btn-primary btn-large" @click="closeSuccessModal">
            Voir les observations
          </button>
        </div>
      </div>
    </div>

    <!-- Disconnect Young Modal -->
    <div v-if="showDisconnectModal" class="modal-overlay" @click="cancelDisconnect">
      <div class="modal-content confirm-modal" @click.stop>
        <div class="modal-header">
          <h2>Déconnecter un jeune</h2>
          <button class="modal-close" @click="cancelDisconnect">✕</button>
        </div>
        <div class="modal-body">
          <div class="warning-box">
            <span class="warning-icon">⚠️</span>
            <div>
              <p class="warning-title">Attention</p>
              <p class="warning-text">
                Cette action est irréversible et aura les conséquences suivantes :
              </p>
            </div>
          </div>
          <ul class="consequence-list">
            <li>Vous ne pourrez plus accéder au profil de <strong>{{ profile.user.firstName }}</strong></li>
            <li>Toutes vos observations seront conservées mais ne seront plus accessibles</li>
            <li>Le jeune devra vous envoyer un nouveau code d'invitation pour rétablir la connexion</li>
          </ul>
          <p class="info-text">
            Êtes-vous sûr de vouloir déconnecter <strong>{{ profile.user.firstName }}</strong> ?
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="cancelDisconnect">Annuler</button>
          <button
            class="btn-danger"
            @click="executeDisconnect"
            :disabled="disconnectLoading"
          >
            <span v-if="disconnectLoading" class="btn-spinner"></span>
            {{ disconnectLoading ? 'Déconnexion...' : 'Déconnecter' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AuthService from '@/services/AuthService';
import accompanimentService from '@/services/accompaniment.service';
import ObservationCard from '@/components/accompaniment/ObservationCard.vue';
import ObservationForm from '@/components/accompaniment/ObservationForm.vue';
import ObservationDetail from '@/components/accompaniment/ObservationDetail.vue';
import StarRating from '@/components/accompaniment/StarRating.vue';
import EditableStarRating from '@/components/accompaniment/EditableStarRating.vue';
import badgeScenario from '@/assets/badges_skills/Badge_jeu_de_scénarios.png';
import badgeShapeSequence from '@/assets/badges_skills/Badge_jeu_de_formes.png';
import badgeJobs from '@/assets/badges_skills/Badge_jeu_de_metiers.png';
import badgeSpeed from '@/assets/badges_skills/Badge_jeu_de_vitesse.png';
import badgeSkills from '@/assets/badges_skills/Badge_jeu_des_compétences.png';
import badgeEnvironment from '@/assets/badges_skills/Badge_jeu_de_l\'environnement.png';
import defaultBadgeImage from '@/assets/badges_skills/Badge_tous_les_jeux_finis.png';

const BADGE_IMAGES_BY_GAME = {
  scenario: badgeScenario,
  shapeSequence: badgeShapeSequence,
  jobs: badgeJobs,
  speed: badgeSpeed,
  skills: badgeSkills,
  roomEnv: badgeEnvironment
};

const DEFAULT_BADGE_IMAGE = defaultBadgeImage;

export default {
  name: 'YoungPersonProfile',
  components: {
    ObservationCard,
    ObservationForm,
    ObservationDetail,
    StarRating,
    EditableStarRating
  },
  data() {
    return {
      loading: true,
      profile: null,
      currentUserId: null,
      currentUserEmail: null,
      recentObservations: [],
      totalObservationsCount: 0,
      gamesData: {},
      competenceBlocks: [],
      profilePicture: null,
      skillsAssessment: null,
      availableSkillsForAccompanist: [], // List of unique skill names that the accompanist can evaluate
      accompanistRatings: {},
      showEvaluationModal: false,
      showInputMethodModal: false,
      showObservationForm: false,
      showObservationDetail: false,
      showSuccessModal: false,
      showConfirmDeleteModal: false,
      showDisconnectModal: false,
      disconnectLoading: false,
      connectionId: null,
      successMessageData: null,
      selectedObservation: null,
      editingObservation: null,
      observationToDelete: null,
      preselectedCompetence: null,
      openInVocalMode: false,
      currentSkill: '',
      currentSkillCategory: '',
      expandedComments: {},
      expandedOtherComments: {},
      evaluationForm: {
        rating: 0,
        comment: ''
      },
      activeTab: 'overview',
      tabs: [
        { id: 'overview', label: 'Vue d\'ensemble', icon: '📊' },
        { id: 'observations', label: 'Observations', icon: '📝' },
        { id: 'competences', label: 'Compétences', icon: '✨' },
        { id: 'games', label: 'Parcours jeux', icon: '🎮' }
      ],
      // Filtres pour les compétences
      skillsFilter: 'all', // all, evaluated, non-evaluated
      selectedCategoryFilter: 'all', // all, Strong, WantToLearn, Weak, Unknown
      showOtherEvaluations: true
    };
  },
  computed: {
    fullName() {
      return `${this.profile?.user.firstName}`;
    },
    initials() {
      const first = this.profile?.user.firstName?.[0] || '';
      const last = this.profile?.user.lastName?.[0] || '';
      return `${first}${last}`.toUpperCase();
    },
    filteredGamesData() {
      // Filter out the scenario game from the list
      const filtered = {};
      Object.keys(this.gamesData).forEach(key => {
        if (key !== 'scenario') {
          filtered[key] = this.gamesData[key];
        }
      });
      return filtered;
    },
    unlockedBadges() {
      // Retourner les badges pour les jeux complétés à 100%
      const badges = [];
      Object.keys(this.gamesData).forEach(key => {
        const game = this.gamesData[key];
        if (game.completion >= 1.0) {
          badges.push({
            key: key,
            name: this.getGameName(key),
            image: this.getBadgeImageForGame(key)
          });
        }
      });
      return badges;
    },
    overallGameProgress() {
      // Calculer la progression globale moyenne de tous les jeux
      const games = Object.values(this.gamesData);
      if (games.length === 0) return 0;

      const totalCompletion = games.reduce((sum, game) => sum + (game.completion || 0), 0);
      return Math.round((totalCompletion / games.length) * 100);
    },
    skillsByCategory() {
      // Collect all unique skills from both self-assessment and accompanist ratings
      const allSkills = new Set();

      // 1. From self-assessment
      if (this.skillsAssessment?.data?.skillsAssessment) {
        Object.values(this.skillsAssessment.data.skillsAssessment).forEach(categorySkills => {
          Object.keys(categorySkills).forEach(skill => allSkills.add(skill));
        });
      }

      // 2. From accompanist ratings
      if (this.availableSkillsForAccompanist) {
        this.availableSkillsForAccompanist.forEach(skill => allSkills.add(skill));
      }
      
      // Also check accompanistRatings object keys just in case
      Object.keys(this.accompanistRatings).forEach(skill => allSkills.add(skill));

      if (allSkills.size === 0) return null;

      return [{
        key: 'AllSkills',
        label: 'Compétences du profil',
        icon: '📋',
        color: '#6366F1', // Indigo
        skills: Array.from(allSkills).sort()
      }];
    },
    
    filteredSkillsByCategory() {
      if (!this.skillsByCategory) return null;

      // Filtrer par catégorie sélectionnée
      if (this.selectedCategoryFilter !== 'all') {
        return this.skillsByCategory.filter(cat => cat.key === this.selectedCategoryFilter);
      }

      return this.skillsByCategory;
    },
    skillsAssessmentCompletion() {
      if (!this.skillsAssessment) {
        return null;
      }

      const completion = this.skillsAssessment.data?.completion;
      return typeof completion === 'number' ? completion : 0;
    },
    totalSkillsFromAssessment() {
      // Compter le nombre total de compétences disponibles pour l'évaluation par l'accompagnant
      return this.availableSkillsForAccompanist.length;
    },
    evaluatedSkillsCount() {
      // Compter le nombre de compétences évaluées par l'accompagnant
      return Object.values(this.accompanistRatings).filter(entry => 
        entry && entry.myRating && entry.myRating.rating !== null && entry.myRating.rating > 0
      ).length;
    },
    notEvaluatedSkillsCount() {
      // Compétences de l'auto-évaluation pas encore évaluées
      return this.totalSkillsFromAssessment - this.evaluatedSkillsCount;
    },
    totalCompetences() {
      // Calculer le nombre total de compétences
      if (!this.profile?.competenceValidations) return 0;
      return this.profile.competenceValidations.reduce((sum, validation) => {
        return sum + (validation.competences?.length || 0);
      }, 0);
    },
    totalCompetencesValidated() {
      // Calculer le nombre de compétences validées
      if (!this.profile?.competenceValidations) return 0;
      return this.profile.competenceValidations.reduce((sum, validation) => {
        const validated = validation.competences?.filter(c => c.validated).length || 0;
        return sum + validated;
      }, 0);
    },
    displayCompetencesValue() {
      // Priorité aux compétences du référentiel si elles existent
      if (this.totalCompetences > 0) {
      return `${this.totalCompetencesValidated}/${this.totalCompetences}`;
      }
      // Sinon, on affiche l'avancement de l'évaluation des compétences auto-déclarées
      return `${this.evaluatedSkillsCount}/${this.totalSkillsFromAssessment}`;
    },
    displayCompetencesLabel() {
      if (this.totalCompetences > 0) {
      return 'Compétences validées';
      }
      // Libellé adapté pour l'auto-évaluation + évaluation accompagnant
      return 'Compétences évaluées';
    },
    displayCompetencesProgress() {
      if (this.totalCompetences > 0) {
      return this.competencesValidationProgress;
      }
      if (this.totalSkillsFromAssessment > 0) {
      return Math.round((this.evaluatedSkillsCount / this.totalSkillsFromAssessment) * 100);
      }
      return 0;
    },
    competencesNonValidated() {
      // Compétences non validées (toutes celles qui ne sont pas validées)
      if (!this.profile?.competenceValidations) return 0;
      return this.profile.competenceValidations.reduce((sum, validation) => {
        const nonValidated = validation.competences?.filter(c => !c.validated).length || 0;
        return sum + nonValidated;
      }, 0);
    },
    competencesToEvaluate() {
      // Compétences sans observations
      if (!this.profile?.competenceValidations) return 0;
      return this.profile.competenceValidations.reduce((sum, validation) => {
        const toEvaluate = validation.competences?.filter(c => !c.validated && c.observationsCount === 0).length || 0;
        return sum + toEvaluate;
      }, 0);
    },
    competencesInProgress() {
      // Compétences avec observations mais pas encore validées
      if (!this.profile?.competenceValidations) return 0;
      return this.profile.competenceValidations.reduce((sum, validation) => {
        const inProgress = validation.competences?.filter(c => !c.validated && c.observationsCount > 0).length || 0;
        return sum + inProgress;
      }, 0);
    },
    competencesValidationProgress() {
      // Pourcentage de compétences validées
      if (this.totalCompetences === 0) return 0;
      return Math.round((this.totalCompetencesValidated / this.totalCompetences) * 100);
    },
    profileSummaryText() {
      // Générer un texte de synthèse détaillé basé sur toutes les données
      if (!this.profile) return '';

      const firstName = this.profile.user.firstName;
      let summary = [];

      // Introduction basée sur la progression globale
      const globalProgress = Math.round((this.overallGameProgress + this.competencesValidationProgress) / 2);
      if (globalProgress >= 75) {
        summary.push(`${firstName} démontre une excellente progression dans son parcours d'accompagnement.`);
      } else if (globalProgress >= 50) {
        summary.push(`${firstName} évolue positivement dans son parcours d'accompagnement.`);
      } else if (globalProgress > 20) {
        summary.push(`${firstName} a bien débuté son parcours d'accompagnement.`);
      } else if (globalProgress > 0) {
        summary.push(`${firstName} commence son parcours d'accompagnement.`);
      } else {
        summary.push(`${firstName} débute son parcours. Les premières observations permettront de mieux identifier ses compétences et axes de développement.`);
      }

      // Détail des jeux complétés avec analyse
      const completedGames = Object.keys(this.gamesData).filter(key => this.gamesData[key].completion >= 1.0);
      const inProgressGames = Object.keys(this.gamesData).filter(key => this.gamesData[key].completion > 0 && this.gamesData[key].completion < 1.0);

      if (completedGames.length > 0) {
        const gameNames = completedGames.map(key => this.getGameName(key)).join(', ');
        summary.push(`${completedGames.length === 1 ? 'Le jeu' : 'Les jeux'} ${gameNames} ${completedGames.length === 1 ? 'a été complété' : 'ont été complétés'} à 100%, démontrant une persévérance et un engagement remarquables.`);
      }

      if (inProgressGames.length > 0) {
        const avgProgress = Math.round(inProgressGames.reduce((sum, key) => sum + (this.gamesData[key].completion * 100), 0) / inProgressGames.length);
        summary.push(`${inProgressGames.length} ${inProgressGames.length > 1 ? 'jeux' : 'jeu'} ${inProgressGames.length > 1 ? 'sont' : 'est'} en cours de réalisation avec une progression moyenne de ${avgProgress}%.`);
      }

      // Analyse des compétences avec contexte
      if (this.totalCompetences > 0) {
        const validationRate = this.competencesValidationProgress;
        if (validationRate >= 50) {
          summary.push(`Avec ${this.totalCompetencesValidated} compétences validées sur ${this.totalCompetences} (${validationRate}%), ${firstName} démontre une solide acquisition de compétences professionnelles.`);
        } else if (this.totalCompetencesValidated > 0) {
          summary.push(`${this.totalCompetencesValidated} compétence${this.totalCompetencesValidated > 1 ? 's ont été validées' : ' a été validée'} sur ${this.totalCompetences}. Les observations futures permettront de valider progressivement les compétences restantes.`);
        } else if (this.competencesToEvaluate > 0) {
          summary.push(`${this.competencesToEvaluate} compétence${this.competencesToEvaluate > 1 ? 's sont' : ' est'} à observer et évaluer. C'est l'occasion de documenter les capacités de ${firstName} dans des situations réelles.`);
        }
      }

      // Observations et qualité de l'accompagnement
      if (this.recentObservations.length > 0) {
        summary.push(`L'accompagnement bénéficie de ${this.recentObservations.length} observation${this.recentObservations.length > 1 ? 's' : ''} documentée${this.recentObservations.length > 1 ? 's' : ''}, permettant un suivi précis et personnalisé de l'évolution.`);

        // Analyse des compétences les plus observées (si disponible)
        if (this.profile.competenceValidations && this.profile.competenceValidations.length > 0) {
          const mostObservedCompetences = [];
          this.profile.competenceValidations.forEach(validation => {
            validation.competences?.forEach(comp => {
              if (comp.observationsCount > 0) {
                mostObservedCompetences.push({ name: comp.name, count: comp.observationsCount });
              }
            });
          });

          if (mostObservedCompetences.length > 0) {
            mostObservedCompetences.sort((a, b) => b.count - a.count);
            const topCompetence = mostObservedCompetences[0];
            if (topCompetence.count >= 3) {
              summary.push(`La compétence "${topCompetence.name}" fait l'objet d'un suivi particulièrement approfondi avec ${topCompetence.count} observations.`);
            }
          }
        }
      }

      // Réussites et badges
      if (this.unlockedBadges.length > 0) {
        summary.push(`${this.unlockedBadges.length} badge${this.unlockedBadges.length > 1 ? 's ont été débloqués' : ' a été débloqué'}, témoignant de la réussite et de la motivation de ${firstName}.`);
      }

      // Passions et centres d'intérêt
      if (this.profile.passions && this.profile.passions.length > 0) {
        summary.push(`Ses centres d'intérêt incluent : ${this.profile.passions.join(', ')}, ce qui peut être un levier pour l'engagement et la motivation.`);
      }

      // Auto-évaluation des compétences
      if (this.skillsByCategory && this.skillsByCategory.length > 0) {
        const strongSkills = this.skillsByCategory.find(cat => cat.key === 'Strong');
        const wantToLearnSkills = this.skillsByCategory.find(cat => cat.key === 'WantToLearn');

        if (strongSkills && strongSkills.skills.length > 0) {
          summary.push(`${firstName} a identifié ${strongSkills.skills.length} compétence${strongSkills.skills.length > 1 ? 's' : ''} qu'il/elle possède déjà.`);
        }

        if (wantToLearnSkills && wantToLearnSkills.skills.length > 0) {
          summary.push(`${wantToLearnSkills.skills.length} compétence${wantToLearnSkills.skills.length > 1 ? 's sont identifiées comme axes' : ' est identifiée comme axe'} de développement souhaité${wantToLearnSkills.skills.length > 1 ? 's' : ''}.`);
        }
      }

      // Recommandations pour la suite
      if (this.competencesToEvaluate > this.totalCompetencesValidated && this.recentObservations.length < 5) {
        summary.push(`Pour enrichir le profil, il serait bénéfique d'augmenter le nombre d'observations afin de documenter davantage de compétences.`);
      }

      return summary.length > 0 ? summary.join(' ') : `${firstName} débute son parcours. Les données de progression apparaîtront au fur et à mesure de son utilisation de l'application et des observations réalisées.`;
    }
  },
  async mounted() {
    await this.loadProfile();
    window.scrollTo(0, 0);
  },
  beforeUnmount() {
    // Nettoyer l'URL blob pour éviter les fuites mémoire
    if (this.profilePicture && this.profilePicture.startsWith('blob:')) {
      URL.revokeObjectURL(this.profilePicture);
    }
  },
  methods: {
    async loadProfile() {
      try {
        this.loading = true;
        const youngId = this.$route.params.id;

        // Get current user ID to filter ratings
        try {
                    const userProfileResponse = await AuthService.request('get', '/user/profile');
                    this.currentUserId = userProfileResponse.data.id || userProfileResponse.data.user_id;
                    this.currentUserEmail = userProfileResponse.data.email || userProfileResponse.data.username;
                    console.log('DEBUG: User info:', { id: this.currentUserId, email: this.currentUserEmail });
                  } catch (error) {
                    console.error('Error fetching current user profile:', error);
                  }
          
                  // Load profile and games data (using real API)
                  const [profileResponse, gamesResponse] = await Promise.all([
                    accompanimentService.getYoungProfile(youngId),
                    accompanimentService.getYoungGamesData(youngId)
                  ]);
          
                  this.profile = profileResponse.data;
                  this.gamesData = gamesResponse.data;
          
                  // Fetch connectionId
                  try {
                    const connectionsResponse = await accompanimentService.getYoungUsers();
                    const connection = connectionsResponse.data.find(c => c.id == youngId);
                    if (connection) {
                      this.connectionId = connection.connectionId;
                    }
                  } catch (error) {
                    console.warn('Could not fetch connection ID:', error);
                  }

                  // Load skills assessment
                  try {
                    const skillsResponse = await accompanimentService.getYoungSkillsAssessment(youngId);
                    this.skillsAssessment = skillsResponse.data;
                  } catch (error) {
                    console.warn('Skills assessment not available:', error);
                  }
          
                  // Load skill ratings from accompanists
                  try {
                    const ratingsResponse = await accompanimentService.getSkillRatings(youngId);
          // Transform API response to local format
          this.accompanistRatings = {};
          if (ratingsResponse.data && ratingsResponse.data.ratings) {
            console.log('DEBUG: Total ratings received from API:', ratingsResponse.data.ratings.length);
            
            // Extract all unique skill names from the response as the base for evaluable skills
            const uniqueSkillNames = new Set(ratingsResponse.data.ratings.map(r => r.skill_name));
            this.availableSkillsForAccompanist = Array.from(uniqueSkillNames);

            ratingsResponse.data.ratings.forEach(rating => {
              const skill = rating.skill_name;
              if (!this.accompanistRatings[skill]) {
                this.accompanistRatings[skill] = { myRating: null, others: [] };
              }

              const isMine = (this.currentUserId && String(rating.issuer.user_id) === String(this.currentUserId)) ||
                             (this.currentUserEmail && rating.issuer.username === this.currentUserEmail);

              const ratingObj = {
                id: rating.id,
                rating: rating.rating,
                comment: rating.description,
                dateCreated: rating.date_created,
                dateUpdated: rating.date_updated,
                issuer: rating.issuer
              };

              if (isMine) {
                this.accompanistRatings[skill].myRating = ratingObj;
              } else if (rating.rating && rating.rating > 0) {
                this.accompanistRatings[skill].others.push(ratingObj);
              }
            });
            console.log('DEBUG: Populated accompanistRatings:', this.accompanistRatings);
          } else {
            this.availableSkillsForAccompanist = [];
          }
        } catch (error) {
          console.warn('Skill ratings not available:', error);
          this.accompanistRatings = {};
        }

        // Load observations (using real API)
        try {
          const observationsResponse = await accompanimentService.getRecentObservations(youngId);
          this.recentObservations = observationsResponse.data || [];
          this.totalObservationsCount = observationsResponse.totalCount || this.recentObservations.length;
        } catch (error) {
          console.warn('Observations not available:', error);
          this.recentObservations = [];
          this.totalObservationsCount = 0;
        }

        // Set empty arrays for data not yet available from API
        this.competenceBlocks = [];

        // Load profile picture if available
        if (this.profile.user.hasProfilePicture) {
          try {
            const pictureResponse = await accompanimentService.getYoungProfilePicture(youngId);
            this.profilePicture = pictureResponse.data;
          } catch (error) {
            console.warn('Profile picture not available:', error);
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        this.loading = false;
      }
    },
    getBlockInfo(blockId) {
      return this.competenceBlocks.find(b => b.id === blockId) || {};
    },
    getGameName(key) {
      const names = {
        scenario: 'Jeu de Scénarios',
        shapeSequence: 'Séquence de formes',
        jobs: 'Exploration métiers',
        speed: 'Jeu de rapidité',
        skills: 'Roue des compétences',
        roomEnv: 'Environnement idéal'
      };
      return names[key] || key;
    },
    getBadgeImageForGame(gameKey) {
      return BADGE_IMAGES_BY_GAME[gameKey] || DEFAULT_BADGE_IMAGE;
    },
    formatTraitName(key) {
      const names = {
        empathie: 'Empathie',
        diplomatie: 'Diplomatie',
        assertivite: 'Assertivité',
        pragmatisme: 'Pragmatisme'
      };
      return names[key] || key;
    },
    goBack() {
      this.$router.push({ name: 'AccompanistDashboard' });
    },
    newObservation() {
      this.preselectedCompetence = null;
      this.showInputMethodModal = true;
    },
    newObservationForCompetence(competence) {
      this.preselectedCompetence = competence.key;
      this.showInputMethodModal = true;
    },
    closeInputMethodModal() {
      this.showInputMethodModal = false;
    },
    chooseVoiceInput() {
      this.closeInputMethodModal();
      this.openInVocalMode = true;
      this.showObservationForm = true;
    },
    chooseManualInput() {
      this.closeInputMethodModal();
      this.openInVocalMode = false;
      this.showObservationForm = true;
    },
    closeObservationForm() {
      this.showObservationForm = false;
      this.preselectedCompetence = null;
      this.openInVocalMode = false;
      this.editingObservation = null;
    },
    async handleObservationSubmit(observationData) {
      try {
        // Déterminer si on crée ou met à jour
        if (this.editingObservation) {
          // Mettre à jour l'observation existante
          await accompanimentService.updateObservation(this.editingObservation.id, observationData);
        } else {
          // Créer une nouvelle observation
          await accompanimentService.createObservation(observationData);
        }

        // Fermer le formulaire
        this.closeObservationForm();

        // Recharger les observations
        await this.loadProfile();

        // Préparer les données pour le message de succès
        const competences = observationData.competences || [];
        const competencesList = competences.length > 0 ? competences.join(', ') : 'Observation libre';
        
        this.successMessageData = {
          competences: competencesList,
          date: new Date(observationData.date).toLocaleDateString('fr-FR'),
          duration: observationData.duration || 'Non spécifiée'
        };
        
        this.showSuccessModal = true;

      } catch (error) {
        console.error('Error creating observation:', error);
        alert('❌ Erreur lors de l\'enregistrement de l\'observation.\n\nVeuillez réessayer ou contacter le support si le problème persiste.');
      }
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      this.successMessageData = null;
      // Changer vers l'onglet observations pour voir le résultat
      this.activeTab = 'observations';
    },
    viewObservationDetails(observation) {
      // Charger les données complètes de l'observation
      if (observation.fullData) {
        this.selectedObservation = {
          ...observation.fullData,
          id: observation.id,
          date: observation.date,
          observedBy: observation.observedBy
        };
      } else {
        this.selectedObservation = observation;
      }
      this.showObservationDetail = true;
    },
    closeObservationDetail() {
      this.showObservationDetail = false;
      this.selectedObservation = null;
    },
    editObservation(observation) {
      // Fermer le détail et ouvrir le formulaire avec les données de l'observation
      this.closeObservationDetail();

      // Si l'observation contient fullData (cas de la liste), on l'utilise
      if (observation.fullData) {
        this.editingObservation = {
          ...observation.fullData,
          id: observation.id,
          date: observation.date,
          observedBy: observation.observedBy
        };
      } else {
        // Sinon (cas du détail déjà ouvert), on utilise l'objet tel quel
        this.editingObservation = observation;
      }

      // Ouvrir le formulaire d'observation
      this.showObservationForm = true;
    },
    deleteObservation(observation) {
      // Stocker l'observation à supprimer et afficher le modal de confirmation
      this.observationToDelete = observation;
      this.showConfirmDeleteModal = true;
    },
    cancelDelete() {
      // Annuler la suppression
      this.showConfirmDeleteModal = false;
      this.observationToDelete = null;
    },
    confirmDisconnect() {
      this.showDisconnectModal = true;
    },
    cancelDisconnect() {
      this.showDisconnectModal = false;
      this.disconnectLoading = false;
    },
    async executeDisconnect() {
      if (!this.connectionId) {
        console.error('No connection ID found for young person');
        this.cancelDisconnect();
        return;
      }

      try {
        this.disconnectLoading = true;

        // Appel au service pour déconnecter
        await accompanimentService.disconnectYoung(this.connectionId);

        // Fermer le modal
        this.cancelDisconnect();

        // Rediriger vers le tableau de bord après déconnexion
        this.$router.push({ name: 'AccompanistDashboard' });

        console.log('Jeune déconnecté avec succès');
      } catch (error) {
        console.error('Error disconnecting young person:', error);
        alert('Une erreur est survenue lors de la déconnexion. Veuillez réessayer.');
      } finally {
        this.disconnectLoading = false;
      }
    },
    async confirmDelete() {
      // Confirmer et effectuer la suppression
      if (!this.observationToDelete) return;

      try {
        // Appeler le service pour supprimer l'observation
        await accompanimentService.deleteObservation(this.observationToDelete.id);

        // Fermer les modals
        this.showConfirmDeleteModal = false;
        this.closeObservationDetail();

        // Recharger les observations
        await this.loadProfile();

        // Afficher un message de succès
        this.successMessageData = {
          title: 'Observation supprimée',
          message: 'L\'observation a été supprimée avec succès.',
          showViewObservations: false
        };
        this.showSuccessModal = true;

        // Nettoyer
        this.observationToDelete = null;
      } catch (error) {
        console.error('Error deleting observation:', error);
        this.showConfirmDeleteModal = false;
        this.observationToDelete = null;
        alert('Une erreur est survenue lors de la suppression de l\'observation.');
      }
    },
    getSkillSelfAssessment(skillName) {
      if (!skillName || typeof skillName !== 'string') return null;
      if (!this.skillsAssessment?.data?.skillsAssessment) return null;
      
      const assessmentData = this.skillsAssessment.data.skillsAssessment;
      
      const categories = {
        'Strong': { label: 'Je la possède', icon: '💪', color: '#10B981', class: 'bg-green-100 text-green-800' },
        'WantToLearn': { label: 'Je veux la développer', icon: '🌱', color: '#3B82F6', class: 'bg-blue-100 text-blue-800' },
        'Weak': { label: "J'ai du mal avec", icon: '🔍', color: '#F59E0B', class: 'bg-yellow-100 text-yellow-800' },
        'Unknown': { label: 'Je ne sais pas', icon: '❓', color: '#6B7280', class: 'bg-gray-100 text-gray-800' }
      };

      for (const key in categories) {
        if (assessmentData[key] && Object.prototype.hasOwnProperty.call(assessmentData[key], skillName)) {
          return categories[key];
        }
      }
      return null;
    },

    openEvaluationModal(skillName, categoryLabel) {
      this.currentSkill = skillName;
      this.currentSkillCategory = categoryLabel;

      // Load existing evaluation if any
      const entry = this.accompanistRatings[skillName];
      if (entry && entry.myRating) {
        this.evaluationForm.rating = entry.myRating.rating;
        this.evaluationForm.comment = entry.myRating.comment || '';
      } else {
        this.evaluationForm.rating = 0;
        this.evaluationForm.comment = '';
      }

      this.showEvaluationModal = true;
    },
    closeEvaluationModal() {
      this.showEvaluationModal = false;
      this.currentSkill = '';
      this.currentSkillCategory = '';
      this.evaluationForm = {
        rating: 0,
        comment: ''
      };
    },
    async saveEvaluation() {
      if (this.evaluationForm.rating === 0) {
        return;
      }

      try {
        // Call backend API to save evaluation
        const response = await accompanimentService.createSkillRating(
          this.profile.user.id,
          this.currentSkill,
          this.evaluationForm.rating,
          this.evaluationForm.comment || null
        );

        // Update local state with response data
        if (!this.accompanistRatings[this.currentSkill]) {
          this.accompanistRatings[this.currentSkill] = { myRating: null, others: [] };
        }

        this.accompanistRatings[this.currentSkill].myRating = {
          rating: this.evaluationForm.rating,
          comment: this.evaluationForm.comment,
          dateCreated: response.data.date_created || new Date().toISOString(),
          dateUpdated: response.data.date_updated || new Date().toISOString(),
          issuer: response.data.issuer
        };

        this.closeEvaluationModal();

      } catch (error) {
        console.error('Error saving evaluation:', error);
        alert('❌ Erreur lors de l\'enregistrement de l\'évaluation.\n\nVeuillez réessayer ou contacter le support si le problème persiste.');
      }
    },
    async deleteEvaluation() {
      // Confirm deletion
      if (!confirm(`Êtes-vous sûr de vouloir supprimer votre évaluation de "${this.currentSkill}" ?\n\nCette action est irréversible.`)) {
        return;
      }

      try {
        // Call backend API to delete evaluation
        await accompanimentService.deleteSkillRating(
          this.profile.user.id,
          this.currentSkill
        );

        // Remove from local state (only my rating)
        if (this.accompanistRatings[this.currentSkill]) {
          this.accompanistRatings[this.currentSkill].myRating = null;
        }

        this.closeEvaluationModal();

        // Show success message
        alert(`✅ Évaluation supprimée avec succès !\n\nCompétence : ${this.currentSkill}`);
      } catch (error) {
        console.error('Error deleting evaluation:', error);
        alert('❌ Erreur lors de la suppression de l\'évaluation.\n\nVeuillez réessayer ou contacter le support si le problème persiste.');
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    toggleComment(skill) {
      // Ne rien faire si la compétence n'a pas de commentaire
      if (!this.accompanistRatings[skill]?.myRating?.comment) {
        return;
      }
      // Toggle l'état d'expansion
      this.expandedComments[skill] = !this.expandedComments[skill];
    },
    toggleOtherComment(ratingId) {
      if (!ratingId) return;
      this.expandedOtherComments[ratingId] = !this.expandedOtherComments[ratingId];
    },
    getEvaluatedSkills(skills) {
      // Retourne les compétences qui ont déjà été évaluées (rating > 0)
      return skills.filter(skill => {
        const entry = this.accompanistRatings[skill];
        return entry && entry.myRating && entry.myRating.rating !== null && entry.myRating.rating > 0;
      });
    },
    getNonEvaluatedSkills(skills) {
      // Retourne les compétences qui n'ont pas encore été évaluées (pas de rating ou rating <= 0)
      return skills.filter(skill => {
        const entry = this.accompanistRatings[skill];
        return !entry || !entry.myRating || entry.myRating.rating === null || entry.myRating.rating <= 0;
      });
    },
    shouldShowEvaluated() {
      // Afficher les compétences évaluées si le filtre est "all" ou "evaluated"
      return this.skillsFilter === 'all' || this.skillsFilter === 'evaluated';
    },
    shouldShowNonEvaluated() {
      // Afficher les compétences non évaluées si le filtre est "all" ou "non-evaluated"
      return this.skillsFilter === 'all' || this.skillsFilter === 'non-evaluated';
    },
    shouldShowCategory(category) {
      // Afficher la catégorie si elle a au moins une compétence visible selon les filtres
      const hasEvaluated = this.shouldShowEvaluated() && this.getEvaluatedSkills(category.skills).length > 0;
      const hasNonEvaluated = this.shouldShowNonEvaluated() && this.getNonEvaluatedSkills(category.skills).length > 0;
      return hasEvaluated || hasNonEvaluated;
    }
  }
};
</script>

<style scoped>
.young-person-profile {
  min-height: 100vh;
  background: linear-gradient(to bottom, #F9FAFB 0%, #FFFFFF 100%);
  padding: 2rem;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  text-align: center;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #E5E7EB;
  border-top-color: #FF9800;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.profile-container {
  max-width: 1400px;
  margin: 0 auto;
}

.profile-header {
  background: white;
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
}

.btn-back {
  background: white;
  border: 2px solid #E5E7EB;
  border-radius: 8px;
  padding: 0.625rem 1.25rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  transition: all 0.2s ease;
  position: relative;
  z-index: 10;
}

.btn-back:hover {
  background: #F9FAFB;
  border-color: #D1D5DB;
}

.back-arrow {
  font-size: 1.25rem;
}

.young-person-info {
  display: flex;
  gap: 1.5rem;
  align-items: center;
  margin-bottom: 1.5rem;
}

.avatar-large {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  font-weight: 700;
  flex-shrink: 0;
}

.avatar-large-img {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.person-details {
  flex: 1;
}

.person-name {
  font-size: 2rem;
  font-weight: 800;
  color: #111827;
  margin: 0 0 0.25rem 0;
}

.person-meta {
  font-size: 1rem;
  color: #6B7280;
  margin: 0 0 1rem 0;
}

.attachment-code-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.code-label {
  font-size: 0.875rem;
  color: #6B7280;
  font-weight: 500;
  flex-shrink: 0;
}

.code-value {
  font-size: 0.875rem;
  color: #111827;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  background: #F3F4F6;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  letter-spacing: 1px;
  word-break: break-all;
  max-width: 100%;
}

.passions-list {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}

.passions-label {
  font-size: 0.875rem;
  color: #6B7280;
  font-weight: 500;
}

.passion-badges {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.passion-badge {
  background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
  color: #92400E;
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
}

.accompanists-list {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.accompanists-label {
  font-size: 0.875rem;
  color: #6B7280;
  font-weight: 500;
}

.accompanist-badges {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.accompanist-badge {
  background: #E5E7EB;
  color: #374151;
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
}

.accompanist-badge.type-parent {
  background: #EDE9FE;
  color: #6B21A8;
}

.accompanist-badge.type-educateur {
  background: #D1FAE5;
  color: #065F46;
}

.header-actions {
  position: absolute;
  top: 2rem;
  right: 2rem;
  display: flex;
  gap: 1rem;
}

.btn-export,
.btn-new-observation {
  border: none;
  border-radius: 10px;
  padding: 0.75rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.btn-export {
  background: #F3F4F6;
  color: #374151;
}

.btn-export:hover {
  background: #E5E7EB;
}

.btn-disconnect {
  background: #FEE2E2;
  color: #991B1B;
  border: none;
  border-radius: 10px;
  padding: 0.75rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.btn-disconnect:hover {
  background: #FCA5A5;
}

.btn-new-observation {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.4);
}

.btn-new-observation:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(255, 152, 0, 0.5);
}

.btn-icon {
  font-size: 1.25rem;
}

.tabs-container {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  background: white;
  padding: 0.5rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #D1D5DB #F3F4F6;
}

.tabs-container::-webkit-scrollbar {
  height: 6px;
}

.tabs-container::-webkit-scrollbar-track {
  background: #F3F4F6;
  border-radius: 3px;
}

.tabs-container::-webkit-scrollbar-thumb {
  background: #D1D5DB;
  border-radius: 3px;
}

.tabs-container::-webkit-scrollbar-thumb:hover {
  background: #9CA3AF;
}

.tab-button {
  flex: 1;
  background: transparent;
  border: none;
  border-radius: 8px;
  padding: 0.875rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6B7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  min-width: fit-content;
  white-space: nowrap;
}

.tab-button:hover {
  background: #F9FAFB;
}

.tab-button.active {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
}

.tab-icon {
  font-size: 1.25rem;
}

.tab-content {
  min-height: 400px;
}

.overview-grid {
  display: grid;
  gap: 2rem;
}

.section-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.section-card.full-width {
  grid-column: 1 / -1;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.title-icon {
  font-size: 1.5rem;
}

.blocks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.games-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.game-item {
  padding: 1rem;
  background: #F9FAFB;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.game-item.completed {
  background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
  border: 2px solid #F59E0B;
}

.game-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  align-items: center;
}

.game-name {
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.badge-icon {
  font-size: 1rem;
  animation: bounce 1s ease-in-out infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

.game-completion {
  font-weight: 700;
  color: #FF9800;
  font-size: 0.875rem;
}

.game-completion.completion-full {
  color: #F59E0B;
  font-weight: 800;
}

.game-progress-bar {
  height: 8px;
  background: #E5E7EB;
  border-radius: 4px;
  overflow: hidden;
}

.game-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #FF9800 0%, #FF9800 100%);
  transition: width 0.5s ease;
}

.game-progress-fill.progress-complete {
  background: linear-gradient(90deg, #F59E0B 0%, #F97316 100%);
}

.badges-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
}

.badge-card {
  background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
  border: 2px solid #F59E0B;
  border-radius: 12px;
  padding: 1.5rem 1rem;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2);
}

.badge-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(245, 158, 11, 0.3);
}

.badge-icon-large {
  font-size: 3rem;
  margin-bottom: 0.5rem;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.badge-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #92400E;
  margin-bottom: 0.25rem;
}

.badge-description {
  font-size: 0.75rem;
  color: #78350F;
  font-weight: 500;
}

.observations-list-compact {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.btn-see-all {
  background: #F3F4F6;
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #FF9800;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.5rem;
}

.btn-see-all:hover {
  background: #FFF5F0;
}

.observations-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.btn-primary {
  background: #FF9800;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary:hover {
  background: #FF9800;
  transform: translateY(-2px);
}

.btn-icon-plus {
  display: inline;
}

.btn-text-observation {
  display: inline;
}

.observations-grid {
  display: grid;
  gap: 1.5rem;
}

.empty-observations,
.empty-overview {
  text-align: center;
  padding: 4rem 2rem;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-overview h3 {
  color: #111827;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
}

.empty-overview p {
  color: #6B7280;
  font-size: 1rem;
  margin: 0;
}

.connection-notes {
  background: #F9FAFB;
  border-left: 4px solid #FF9800;
  border-radius: 8px;
  padding: 1.25rem;
}

.connection-notes p {
  margin: 0;
  color: #374151;
  line-height: 1.6;
  font-size: 0.9375rem;
}

.competences-detailed {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.block-section {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.block-header-large {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 2px solid #F3F4F6;
}

.block-icon-large {
  width: 80px;
  height: 80px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  flex-shrink: 0;
}

.block-title-info {
  flex: 1;
}

.block-title-info h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;
}

.block-title-info p {
  color: #6B7280;
  margin: 0;
}

.block-stats-large {
  display: flex;
  gap: 2rem;
}

.stat-item-large {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-item-large .stat-label {
  font-size: 0.75rem;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 0.25rem;
}

.stat-item-large .stat-value {
  font-size: 1.5rem;
  font-weight: 800;
  color: #111827;
}

.competences-table {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.competence-row {
  background: #F9FAFB;
  border-radius: 12px;
  padding: 1.25rem;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.competence-row:hover {
  background: #F3F4F6;
  border-color: #E5E7EB;
}

.competence-row.validated {
  background: #D1FAE5;
  border-color: #10B981;
}

.comp-main-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.comp-name-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.comp-badge-check {
  width: 32px;
  height: 32px;
  background: #10B981;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
}

.comp-name {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
}

.comp-observations-count {
  font-size: 0.75rem;
  color: #6B7280;
  margin-top: 0.25rem;
}

.comp-scores {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.score-item {
  display: flex;
  flex-direction: column;
  min-width: 100px;
}

.score-label {
  font-size: 0.75rem;
  color: #9CA3AF;
}

.score-value {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
}

.progress-indicator {
  flex: 1;
  height: 8px;
  background: #E5E7EB;
  border-radius: 4px;
  overflow: hidden;
}

.progress-indicator .progress-fill {
  height: 100%;
  transition: width 0.5s ease;
}

.comp-actions {
  display: flex;
  justify-content: flex-end;
}

.btn-add-obs {
  background: #FF9800;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-add-obs:hover {
  background: #FF9800;
}

.games-dashboard {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.games-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.overall-progress {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: white;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.progress-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6B7280;
  white-space: nowrap;
}

.progress-bar-inline {
  width: 200px;
  height: 10px;
  background: #E5E7EB;
  border-radius: 5px;
  overflow: hidden;
  position: relative;
}

.progress-fill-inline {
  height: 100%;
  background: linear-gradient(90deg, #FF9800 0%, #FF9800 100%);
  transition: width 0.5s ease;
  border-radius: 5px;
}

.progress-percentage {
  font-size: 1rem;
  font-weight: 800;
  color: #111827;
  min-width: 50px;
  text-align: right;
}

@media (max-width: 768px) {
  .tabs-container {
    scrollbar-width: thin;
    scrollbar-color: #667eea #E5E7EB;
    margin-left: 0;
    margin-right: 0;
  }

  .tabs-container::-webkit-scrollbar {
    height: 4px;
  }

  .tabs-container::-webkit-scrollbar-track {
    background: #E5E7EB;
    border-radius: 2px;
  }

  .tabs-container::-webkit-scrollbar-thumb {
    background: #FF9800;
    border-radius: 2px;
  }

  .tab-button {
    flex: 0 0 auto;
    min-width: 140px;
  }

  .games-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .overall-progress {
    width: 100%;
  }

  .progress-bar-inline {
    flex: 1;
  }
}

.games-grid-all {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
}

.game-simple-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: all 0.3s ease;
}

.game-simple-card.game-completed {
  background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
  border: 2px solid #F59E0B;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.game-simple-card h4 {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.trophy-icon {
  font-size: 1.25rem;
  animation: rotate 3s ease-in-out infinite;
}

@keyframes rotate {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-10deg); }
  75% { transform: rotate(10deg); }
}

.game-completion-circle {
  position: relative;
  display: inline-block;
}

.circle-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.25rem;
  font-weight: 800;
  color: #111827;
}

.circle-text.text-completed {
  color: #F59E0B;
}

.badges-summary-box {
  background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
  border: 2px solid #F59E0B;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.badges-summary-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.summary-icon {
  font-size: 2rem;
}

.badges-summary-header h3 {
  font-size: 1.5rem;
  font-weight: 800;
  color: #92400E;
  margin: 0;
}

.badges-summary-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
}

.badge-display-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(245, 158, 11, 0.25);
  box-shadow: 0 10px 24px rgba(245, 158, 11, 0.18);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.badge-display-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 32px rgba(245, 158, 11, 0.25);
}

.badge-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 0% 0%, rgba(253, 230, 138, 0.45), transparent 70%);
  pointer-events: none;
}

.badge-emblem {
  position: relative;
  z-index: 1;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #F59E0B 0%, #FBBF24 50%, #F59E0B 100%);
  box-shadow: inset 0 2px 4px rgba(255, 255, 255, 0.4);
  animation: badge-sparkle 3s ease-in-out infinite;
  overflow: hidden;
}

.badge-emblem-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.badge-details {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.badge-name {
  font-size: 1rem;
  font-weight: 700;
  color: #78350F;
}

.badge-caption {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #B45309;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

@keyframes badge-sparkle {
  0%, 100% { box-shadow: inset 0 2px 4px rgba(255, 255, 255, 0.4); }
  50% { box-shadow: inset 0 2px 8px rgba(255, 255, 255, 0.65); }
}

.info-box {
  background: #FFF5F0;
  border-left: 4px solid #FF9800;
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  gap: 1rem;
}

.info-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.info-box p {
  margin: 0;
  color: #B85C00;
  line-height: 1.6;
  font-size: 0.875rem;
}

/* Skills Self-Assessment Styles */
.skills-self-assessment-section {
  margin-bottom: 3rem;
}

.assessment-header {
  margin-bottom: 2rem;
}

.assessment-description {
  color: #6B7280;
  font-size: 0.9375rem;
  line-height: 1.6;
  margin: 0.75rem 0 0 0;
}

.skills-filters-container {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1.5rem 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.filter-group {
  margin-bottom: 1rem;
}

.filter-group:last-child {
  margin-bottom: 0;
}

.filter-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.filter-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.filter-btn {
  background: #F3F4F6;
  border: 2px solid #E5E7EB;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.filter-btn:hover {
  background: #E5E7EB;
  border-color: #D1D5DB;
}

.filter-btn.active {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  border-color: #FF9800;
  color: white;
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
}

.skills-categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
}

.skill-category-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.skill-category-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.category-header-skill {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #F3F4F6;
}

.category-icon-skill {
  font-size: 2rem;
}

.category-title-skill {
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0;
}

.skills-section {
  margin-bottom: 1.5rem;
}

.skills-section:last-child {
  margin-bottom: 0;
}

.skills-section-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 1rem 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #F3F4F6;
}

.skills-section-title .section-icon {
  font-size: 1rem;
  font-weight: normal;
}

.skill-row.evaluated {
  background: #F0FDF4;
  border-left-width: 4px;
}

.skill-row.non-evaluated {
  background: #FEFCE8;
  border-left-width: 4px;
}

.skills-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.skill-row {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  border-left: 4px solid;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.skill-row:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.skill-main-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.skill-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.skill-title-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.category-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  width: fit-content;
  letter-spacing: 0.3px;
}

.skill-name {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.4;
}

.btn-evaluate {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
  flex-shrink: 0;
  white-space: nowrap;
  height: fit-content;
}

.btn-evaluate:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.4);
}

.btn-evaluate .btn-icon {
  font-size: 1rem;
}

.existing-evaluation {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: #F9FAFB;
  border-radius: 8px;
  border: 1px solid #E5E7EB;
  transition: all 0.2s ease;
}

.existing-evaluation.has-comment {
  cursor: pointer;
}

.existing-evaluation.has-comment:hover {
  background: #F3F4F6;
  border-color: #D1D5DB;
}

.evaluation-summary {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.evaluation-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.evaluation-label {
  font-size: 0.875rem;
  color: #6B7280;
  font-weight: 600;
}

.evaluation-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.evaluation-date {
  font-size: 0.75rem;
  color: #9CA3AF;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.date-icon {
  font-size: 0.875rem;
}

.comment-indicator {
  font-size: 0.75rem;
  color: #FF9800;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  user-select: none;
}

.comment-icon {
  font-size: 0.875rem;
}

.expand-arrow {
  font-size: 0.625rem;
  transition: transform 0.2s ease;
}

.existing-evaluation.expanded .expand-arrow {
  transform: rotate(90deg);
}

.evaluation-comment {
  font-size: 0.875rem;
  color: #4B5563;
  line-height: 1.6;
  margin: 0;
  padding: 0.875rem;
  background: white;
  border-radius: 6px;
  border-left: 3px solid #FF9800;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  max-width: 100%;
  box-sizing: border-box;
}

/* Animations de transition */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-10px);
  max-height: 0;
}

.slide-down-enter-to {
  opacity: 1;
  transform: translateY(0);
  max-height: 500px;
}

.slide-down-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 500px;
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
  max-height: 0;
}

.empty-skills-assessment {
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.empty-skills-assessment h3 {
  color: #111827;
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
}

.empty-skills-assessment p {
  color: #6B7280;
  margin: 0;
}

/* Evaluation Modal Styles */
.evaluation-modal {
  max-width: 600px;
}

.skill-being-evaluated {
  background: #F9FAFB;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border-left: 4px solid #FF9800;
}

.skill-being-evaluated h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;
}

.skill-context {
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0;
  font-style: italic;
}

.evaluation-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.rating-input-large {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: #F9FAFB;
  border-radius: 12px;
  justify-content: center;
}

.rating-help {
  font-size: 0.75rem;
  color: #6B7280;
  margin: 0;
  line-height: 1.6;
  padding: 0.75rem;
  background: #EFF6FF;
  border-radius: 8px;
}

.form-textarea {
  width: 100%;
  padding: 0.875rem;
  border: 2px solid #E5E7EB;
  border-radius: 8px;
  font-size: 0.9375rem;
  color: #111827;
  font-family: inherit;
  transition: all 0.2s ease;
  resize: vertical;
  box-sizing: border-box;
}

.form-textarea:focus {
  outline: none;
  border-color: #FF9800;
  box-shadow: 0 0 0 3px rgba(255, 152, 0, 0.1);
}

.form-textarea::placeholder {
  color: #9CA3AF;
}

.char-count {
  font-size: 0.75rem;
  color: #9CA3AF;
  text-align: right;
  margin: 0;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 0;
    align-items: stretch;
  }
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #111827;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #9CA3AF;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: #F3F4F6;
  color: #111827;
}

.modal-body {
  margin-bottom: 1.5rem;
}

.modal-footer {
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  align-items: center;
}

.modal-footer-left {
  display: flex;
  gap: 1rem;
}

.modal-footer-right {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.btn-secondary,
.btn-primary,
.btn-delete {
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary {
  background: #F3F4F6;
  color: #374151;
}

.btn-secondary:hover {
  background: #E5E7EB;
}

.btn-primary {
  background: #FF9800;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #FF9800;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-delete {
  background: #FEE2E2;
  color: #991B1B;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-delete:hover {
  background: #FCA5A5;
  color: #7F1D1D;
}

@media (max-width: 768px) {
  /* Padding général de la page */
  .young-person-profile {
    padding: 1rem;
  }

  /* Section observations */
  .observations-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .observations-header .section-title {
    font-size: 1.125rem;
  }

  .observations-header .btn-primary {
    width: auto;
    min-width: auto;
    padding: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .observations-header .btn-primary .btn-text-observation {
    display: none;
  }

  .observations-header .btn-primary .btn-icon-plus {
    font-size: 1.5rem;
    font-weight: 600;
  }

  .observations-grid {
    gap: 1rem;
  }

  .section-card {
    padding: 1.25rem;
  }

  /* Section compétences */
  .skills-filters-container {
    padding: 1rem;
  }

  .filter-buttons {
    flex-direction: column;
  }

  .filter-btn {
    width: 100%;
    text-align: center;
  }

  .skills-categories-grid {
    grid-template-columns: 1fr;
  }

  .skill-category-card {
    padding: 1rem;
  }

  .skill-header-row {
    flex-direction: column;
    gap: 1rem;
  }

  .skill-name {
    font-size: 1rem;
  }

  .btn-evaluate {
    width: 100%;
    justify-content: center;
  }

  .evaluation-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .evaluation-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  /* Modals */
  .modal-content {
    padding: 1.5rem;
  }

  .modal-footer {
    flex-direction: column;
  }

  .modal-footer-left,
  .modal-footer-right {
    width: 100%;
  }

  .btn-secondary,
  .btn-primary,
  .btn-delete {
    width: 100%;
  }

  /* Compétences détaillées */
  .block-section {
    padding: 1.25rem;
  }

  .block-header-large {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .block-icon-large {
    width: 60px;
    height: 60px;
    font-size: 2rem;
  }

  .block-stats-large {
    width: 100%;
    justify-content: space-around;
  }

  .comp-main-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .comp-scores {
    flex-direction: column;
    width: 100%;
    gap: 0.75rem;
  }

  .score-item {
    width: 100%;
  }

  .comp-actions {
    width: 100%;
  }

  .btn-add-obs {
    width: 100%;
  }
}

/* New Overview Styles */

/* Summary Card Styles */
.summary-card {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  color: white;
  border: none;
  box-shadow: 0 4px 20px rgba(255, 152, 0, 0.3);
}

.summary-card .section-title {
  color: white;
  margin-bottom: 2rem;
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.summary-main {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.summary-stat-item {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.summary-stat-item:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.summary-stat-value {
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  line-height: 1;
}

.summary-stat-label {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.summary-progress-bar {
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 0.5rem;
}

.summary-progress-fill {
  height: 100%;
  background: white;
  border-radius: 3px;
  transition: width 0.5s ease;
}

.summary-description {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.summary-description-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.description-icon {
  font-size: 1.5rem;
}

.summary-description h3 {
  font-size: 1.125rem;
  font-weight: 700;
  color: white;
  margin: 0;
}

.summary-description p {
  font-size: 1rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

/* Competences Status Card */
.competences-status-card {
  background: linear-gradient(135deg, #F9FAFB 0%, #FFFFFF 100%);
}

.subsection-title {
  font-size: 1rem;
  font-weight: 600;
  color: #6B7280;
  margin: 0 0 1rem 0;
  padding-left: 0.5rem;
  border-left: 3px solid #FF9800;
}

.skills-assessment-stats {
  margin-bottom: 1.5rem;
}

.skills-assessment-stats.with-margin-top {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid #E5E7EB;
}

.empty-competences-state {
  padding: 2rem;
  text-align: center;
  color: #9CA3AF;
  background: #F9FAFB;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.empty-competences-state p {
  margin: 0;
  font-size: 0.875rem;
}

.competences-status-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.competence-status-item {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.competence-status-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.competence-status-item.validated {
  border-color: #10B981;
}

.competence-status-item.validated .status-icon-wrapper {
  background: #D1FAE5;
}

.competence-status-item.validated .status-icon {
  color: #10B981;
}

.competence-status-item.to-evaluate {
  border-color: #F59E0B;
}

.competence-status-item.to-evaluate .status-icon-wrapper {
  background: #FEF3C7;
}

.competence-status-item.to-evaluate .status-icon {
  color: #F59E0B;
}

.status-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.status-icon {
  font-size: 1.5rem;
  font-weight: 700;
}

.status-content {
  flex: 1;
}

.status-value {
  font-size: 1.75rem;
  font-weight: 800;
  color: #111827;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.status-label {
  font-size: 0.875rem;
  color: #6B7280;
  font-weight: 500;
}

.btn-view-competences {
  width: 100%;
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.875rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
}

.btn-view-competences:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.4);
}

/* Observations Overview Card */
.observations-overview-card {
  background: linear-gradient(135deg, #F9FAFB 0%, #FFFFFF 100%);
}

@media (max-width: 768px) {
  .summary-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .competences-status-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  /* En-tête profil */
  .profile-header {
    padding: 1rem;
  }

  .young-person-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .person-details {
    width: 100%;
  }

  .person-name {
    font-size: 1.5rem;
  }

  .attachment-code-display {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .code-value {
    font-size: 0.75rem;
    padding: 0.375rem 0.625rem;
    letter-spacing: 0.5px;
    max-width: calc(100vw - 5rem);
    overflow-wrap: break-word;
  }

  .summary-stats {
    grid-template-columns: 1fr;
  }

  .summary-stat-value {
    font-size: 2rem;
  }

  .header-actions {
    position: static;
    margin-top: 1rem;
    justify-content: flex-start;
  }

  /* Section observations - petits écrans */
  .observations-header {
    margin-bottom: 1rem;
  }

  .observations-header .section-title {
    font-size: 1rem;
  }

  .observations-header .btn-primary {
    width: 48px;
    height: 48px;
    padding: 0.625rem;
    border-radius: 50%;
    box-shadow: 0 4px 12px rgba(255, 152, 0, 0.4);
  }

  .observations-header .btn-primary .btn-icon-plus {
    font-size: 1.75rem;
  }

  .empty-observations {
    padding: 2rem 1rem;
  }

  .empty-observations h3 {
    font-size: 1.125rem;
  }

  /* Section compétences - petits écrans */
  .assessment-header {
    margin-bottom: 1.5rem;
  }

  .assessment-header .section-title {
    font-size: 1rem;
  }

  .assessment-description {
    font-size: 0.875rem;
  }

  .skills-filters-container {
    padding: 0.75rem;
    margin: 1rem 0;
  }

  .filter-label {
    font-size: 0.8125rem;
  }

  .filter-btn {
    padding: 0.625rem 1rem;
    font-size: 0.8125rem;
  }

  .skill-category-card {
    padding: 0.875rem;
  }

  .category-header-skill {
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
  }

  .category-icon-skill {
    font-size: 1.5rem;
  }

  .category-title-skill {
    font-size: 1rem;
  }

  .skill-row {
    padding: 1rem;
  }

  .skill-name {
    font-size: 0.9375rem;
  }

  .skill-badge {
    font-size: 0.6875rem;
    padding: 0.15rem 0.5rem;
    margin-left: 0;
    margin-top: 0.375rem;
  }

  .skill-title-section {
    gap: 0.5rem;
  }

  .btn-evaluate {
    padding: 0.625rem 1rem;
    font-size: 0.8125rem;
  }

  .existing-evaluation {
    padding: 0.75rem;
  }

  .evaluation-label {
    font-size: 0.8125rem;
  }

  .other-evaluations {
    padding-left: 0.5rem;
  }

  .other-evaluations-title {
    font-size: 0.75rem;
  }

  .other-evaluation-item {
    padding: 0.625rem;
  }

  .evaluator-name {
    font-size: 0.8125rem;
  }

  /* Compétences du référentiel - petits écrans */
  .competences-detailed {
    gap: 1.5rem;
  }

  .block-section {
    padding: 1rem;
  }

  .block-header-large {
    padding-bottom: 1rem;
  }

  .block-title-info h2 {
    font-size: 1.125rem;
  }

  .block-title-info p {
    font-size: 0.875rem;
  }

  .competence-row {
    padding: 1rem;
  }

  .comp-name {
    font-size: 0.9375rem;
  }

  .comp-observations-count {
    font-size: 0.6875rem;
  }

  .btn-add-obs {
    padding: 0.5rem 0.875rem;
    font-size: 0.6875rem;
  }

  /* Cartes de statut de compétences */
  .competences-status-grid {
    gap: 1rem;
  }

  .competence-status-item {
    padding: 1rem;
  }

  .status-icon-wrapper {
    width: 40px;
    height: 40px;
  }

  .status-icon {
    font-size: 1.25rem;
  }

  .status-value {
    font-size: 1.5rem;
  }

  .status-label {
    font-size: 0.8125rem;
  }

  .btn-view-competences {
    padding: 0.75rem 1.25rem;
    font-size: 0.8125rem;
  }

  /* Empty states */
  .empty-skills-assessment {
    padding: 2rem 1rem;
  }

  .empty-skills-assessment h3 {
    font-size: 1.125rem;
  }

  .empty-skills-assessment p {
    font-size: 0.875rem;
  }

  .empty-competences-state {
    padding: 1.5rem;
  }

  .empty-competences-state p {
    font-size: 0.8125rem;
  }
}

/* Observation Form Modal Styles */
.observation-form-modal {
  width: 95%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 0;
  border-radius: 16px;
}

.observation-form-modal::-webkit-scrollbar {
  width: 8px;
}

.observation-form-modal::-webkit-scrollbar-track {
  background: #F3F4F6;
  border-radius: 4px;
}

.observation-form-modal::-webkit-scrollbar-thumb {
  background: #D1D5DB;
  border-radius: 4px;
}

.observation-form-modal::-webkit-scrollbar-thumb:hover {
  background: #9CA3AF;
}

@media (max-width: 1024px) {
  .observation-form-modal {
    width: 98%;
  }
}

@media (max-width: 768px) {
  .observation-form-modal {
    width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }
}

/* Observation Detail Modal Styles */
.observation-detail-modal {
  width: 95%;
  max-width: 1100px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 0;
  border-radius: 20px;
}

.observation-detail-modal::-webkit-scrollbar {
  width: 8px;
}

.observation-detail-modal::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.observation-detail-modal::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  border-radius: 4px;
}

.observation-detail-modal::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
}

@media (max-width: 1024px) {
  .observation-detail-modal {
    width: 98%;
  }
}

@media (max-width: 768px) {
  .observation-detail-modal {
    width: 100%;
    max-height: 100vh;
    height: 100vh;
    border-radius: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    position: relative;
  }
}
/* Success Modal Styles */
.success-modal {
  max-width: 450px;
  text-align: center;
}

.success-body {
  padding: 2.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.success-icon-wrapper {
  width: 80px;
  height: 80px;
  background: #D1FAE5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  animation: popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.success-icon-large {
  font-size: 3rem;
}

@keyframes popIn {
  from { transform: scale(0); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.success-body h2 {
  font-size: 1.75rem;
  color: #065F46;
  margin: 0 0 1.5rem 0;
}

.success-summary {
  width: 100%;
  background: #F9FAFB;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 2rem;
  text-align: left;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #E5E7EB;
}

.summary-row:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.summary-row.full {
  flex-direction: column;
  gap: 0.25rem;
}

.summary-label {
  color: #6B7280;
  font-weight: 500;
  font-size: 0.875rem;
}

.summary-value {
  color: #111827;
  font-weight: 600;
}

.btn-large {
  width: 100%;
  justify-content: center;
  padding: 1rem;
  font-size: 1rem;
}

/* Input Method Modal Styles */
.input-method-modal {
  max-width: 700px;
  width: 90%;
}

.input-method-description {
  text-align: center;
  color: #6B7280;
  font-size: 1rem;
  margin-bottom: 2rem;
}

.input-method-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

.input-method-card {
  background: white;
  border: 3px solid #E5E7EB;
  border-radius: 16px;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  position: relative;
  overflow: hidden;
}

.input-method-card:hover {
  border-color: #FF9800;
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(255, 152, 0, 0.2);
}

.input-method-card:active {
  transform: translateY(-2px);
}

.method-icon {
  font-size: 4rem;
  margin-bottom: 0.5rem;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.voice-icon {
  animation-delay: 0s;
}

.manual-icon {
  animation-delay: 0.5s;
}

.method-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.method-description {
  font-size: 0.9375rem;
  color: #6B7280;
  margin: 0;
  line-height: 1.5;
}

.method-badge {
  display: inline-block;
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  color: white;
  padding: 0.375rem 0.875rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 0.5rem;
}

@media (max-width: 768px) {
  .input-method-options {
    grid-template-columns: 1fr;
  }

  .input-method-modal {
    width: 95%;
    max-width: none;
  }
}

.skill-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.75rem;
  white-space: nowrap;
  border: 1px solid currentColor;
  width: fit-content;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 640px) {
  .skill-badge {
    margin-left: 0;
    white-space: normal;
    max-width: 100%;
  }
}

.other-evaluations {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #E5E7EB;
  padding-left: 1rem;
}

.other-evaluations-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6B7280;
  margin: 0 0 0.5rem 0;
  text-transform: uppercase;
}

.other-evaluation-item {
  background: #F9FAFB;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
}

.other-evaluation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
}

.evaluator-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.other-evaluation-comment {
  font-size: 0.875rem;
  color: #4B5563;
  margin: 0;
  font-style: italic;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  max-width: 100%;
}

.other-evaluation-item.has-comment {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.other-evaluation-item.has-comment:hover {
  background-color: #F3F4F6;
}

/* Confirm Delete Modal Styles */
.confirm-modal {
  max-width: 500px;
}

.warning-icon-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.warning-icon {
  font-size: 4rem;
  animation: pulse 2s ease-in-out infinite;
}

 @keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.confirm-message {
  text-align: center;
  font-size: 1rem;
  color: #374151;
  margin-bottom: 0.5rem;
  line-height: 1.5;
}

.confirm-message strong {
  color: #111827;
  font-weight: 600;
}

.confirm-warning {
  text-align: center;
  font-size: 0.875rem;
  color: #DC2626;
  margin-bottom: 1.5rem;
  font-weight: 500;
}

.btn-danger {
  background: #DC2626;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-danger:hover {
  background: #B91C1C;
}

.btn-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-right: 0.5rem;
}

.warning-box {
  background: #FEF3C7;
  border: 2px solid #F59E0B;
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  text-align: left;
}

.warning-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.warning-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #92400E;
  margin: 0 0 0.25rem 0;
}

.warning-text {
  font-size: 0.875rem;
  color: #92400E;
  margin: 0;
  line-height: 1.4;
}

.consequence-list {
  margin: 0 0 1rem 0;
  padding-left: 1.5rem;
  text-align: left;
}

.consequence-list li {
  color: #6B7280;
  font-size: 0.875rem;
  line-height: 1.6;
  margin-bottom: 0.5rem;
}
</style>
